import { FloorplanRemoteSensor } from "@domain/project/floorplan/floorplan-remote-sensor";
import { FloorplanTransmitter } from "@domain/project/floorplan/floorplan-transmitter";
import { FloorplanTransmitterPlaceholder } from "@domain/project/floorplan/floorplan-transmitter-placeholder";
import { FloorplanWorkspaceKonvaAdapter } from "@project/floorplanner/konva/floorplan-workspace-konva.adapter";
import { IconKonva } from "@project/floorplanner/konva/icon-konva";
import { MonitoringAreaKonva } from "@project/floorplanner/konva/monitoring-area-konva";
import { RemoteSensorWireKonva } from "@project/floorplanner/konva/remote-sensor-wire-konva";
import Konva from "konva";

export interface RemoteSensorKonvaShapes {
  group: Konva.Group;
  remoteSensor: Konva.Image;
  monitoringArea: Konva.Circle;
}

export class RemoteSensorKonva {
  static init(
    transmitter: FloorplanTransmitter | FloorplanTransmitterPlaceholder,
    remoteSensor: FloorplanRemoteSensor,
    remoteSensorIcon: Konva.Image,
    transmitterIcon: Konva.Image,
  ): RemoteSensorKonvaShapes {
    const remoteSensorGroup = this.initRemoteSensorGroup(remoteSensor);

    const monitoringArea = MonitoringAreaKonva.init(transmitter);

    const remoteSensorIconGroup = this.initRemoteSensorIconGroup(remoteSensor, remoteSensorIcon, monitoringArea);
    remoteSensorGroup.add(remoteSensorIconGroup);

    IconKonva.setPosition(remoteSensorIcon, remoteSensor.x, remoteSensor.y, remoteSensor.size, 0, 0);

    MonitoringAreaKonva.setPosition(
      monitoringArea,
      remoteSensorIcon.x(),
      remoteSensorIcon.y(),
      remoteSensorIcon.width() * remoteSensorIcon.scaleX(),
    );

    MonitoringAreaKonva.applyInitialScale(monitoringArea, remoteSensorIcon.scaleX());

    // When the remote sensor is transformed, update the monitoring area from the sensor instead of the product icon
    remoteSensorIcon.on("transform", () => {
      MonitoringAreaKonva.setPosition(
        monitoringArea,
        remoteSensorIcon.x(),
        remoteSensorIcon.y(),
        remoteSensorIcon.width() * remoteSensorIcon.scaleX(),
      );
      MonitoringAreaKonva.setStrokeWidth(monitoringArea, remoteSensorIcon.scaleX());
      transmitter.monitoringAreaRadius = monitoringArea.radius();
    });

    const wire = RemoteSensorWireKonva.init(transmitterIcon, remoteSensorIcon);
    remoteSensorGroup.add(wire);
    this.initEventListener(transmitterIcon, remoteSensorIcon, wire);

    return {
      group: remoteSensorGroup,
      remoteSensor: remoteSensorIcon,
      monitoringArea: monitoringArea,
    };
  }

  static getWire(remoteSensorGroup: Konva.Group): Konva.Line {
    return remoteSensorGroup.getChildren()[1] as Konva.Line;
  }

  private static initRemoteSensorGroup(remoteSensor: FloorplanRemoteSensor) {
    const remoteSensorGroup = new Konva.Group({
      id: remoteSensor.id,
    });
    remoteSensorGroup.setAttr(FloorplanWorkspaceKonvaAdapter.OBJECT_KEY, remoteSensor);
    return remoteSensorGroup;
  }

  private static initRemoteSensorIconGroup(
    remoteSensor: FloorplanRemoteSensor,
    remoteSensorIcon: Konva.Image,
    monitoringArea: Konva.Circle,
  ) {
    const iconGroup = IconKonva.initIconGroup(remoteSensor);
    IconKonva.initIconAttrs(remoteSensorIcon, remoteSensor, 0, 50);
    IconKonva.initTransformEndHandler(remoteSensorIcon, remoteSensor);
    IconKonva.addDragMoveHandler(remoteSensorIcon, (icon: Konva.Image) =>
      MonitoringAreaKonva.setPosition(monitoringArea, icon.x(), icon.y(), icon.width() * icon.scaleX()),
    );
    return iconGroup.add(remoteSensorIcon);
  }

  private static initEventListener(transmitterIcon: Konva.Image, remoteSensorIcon: Konva.Image, wire: Konva.Line) {
    remoteSensorIcon.on("dragmove", () => {
      RemoteSensorWireKonva.updateWire(wire, transmitterIcon, remoteSensorIcon);
    });
    remoteSensorIcon.on("transform", () => {
      RemoteSensorWireKonva.updateWire(wire, transmitterIcon, remoteSensorIcon);
    });
  }
}
